// "Which published marketplace ads are this same property?" — the search modal.
//
// ## What it replaces, and why a modal
//
// The old picker rendered its candidate rows as direct siblings inside the listing form's
// Grid: no container, no scroll, so eight rows shoved the rest of the form down the page.
// Eight was also all there ever was — the response had no total, no cursor and no page,
// and the scan behind it was one fixed window, so anything below it was unreachable at
// any depth. Three narrowing predicates were available (transaction type, one city, ±35%
// price) and none of them was adjustable, while the very same server already answered a
// twenty-filter query for the listings board.
//
// So this is a real search surface: a scrolling list of its own, the board's own filter
// chips and its own filters drawer, free text, sort, and paging.
//
// ## Two axes, kept apart
//
// FILTERS DECIDE THE SET; SAMENESS DECIDES THE ORDER. The default sort is the sameness
// rank and every row carries its score; choosing price or date keeps the badge and obeys
// her. Contradicted candidates are not deleted — they are collected behind a toggle with
// the reason printed on each row, because she is usually looking at that very ad in
// another tab and a silently missing row is indistinguishable from a broken search.
//
// ## Nesting
//
// This Dialog opens from inside NewListingDialog, and the filters drawer opens from
// inside this one. MUI's ModalManager stacks them and only the topmost enforces focus —
// the same arrangement ListingQuickViewDialog already uses from a panel. The body is
// rendered only while `open` so the hook re-seeds from the listing on every opening
// rather than showing the previous session's narrowing.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { Close, Replay, RestartAlt, Search, Tune } from "@material-ui/icons";
import MatchFilterChips from "components/_dashboard/contacts/detail/tabs/matches/MatchFilterChips";
import { areSearchFiltersEqual } from "components/_dashboard/contacts/detail/tabs/matches/contactMatchFilters";
import { fetchListingById } from "services/signaturesListings";
import { numberToCurrency } from "utils/listings/display";
import SourceCandidateRow from "./SourceCandidateRow";
import SourceCandidatesFiltersDrawer from "./SourceCandidatesFiltersDrawer";
import { DEFAULT_CANDIDATE_SORT, useSourceCandidatesSearch } from "./useSourceCandidatesSearch";
import type { SourceCandidateSort, SourceCandidatesTarget } from "./useSourceCandidatesSearch";
import type { REST_schema } from "../../../../@types/sharedSchema";

type SortOptionId = "sameness" | "priceAsc" | "priceDesc" | "newest";

/**
 * Only sorts that ride a matching index are offered.
 *
 * `updatedTime` (both directions), `price` (both) and `listingPublishedTime` desc have
 * indexes shaped for the exact ORDER BY the query emits; the remaining fields sort
 * correctly but fall back to a full sort of the match set. Offering one of those from a
 * dropdown would turn a 30ms search into a second-scale one for no visible reason.
 */
const SORT_OPTIONS: Record<SortOptionId, SourceCandidateSort> = {
  sameness: DEFAULT_CANDIDATE_SORT,
  priceAsc: { field: "price", direction: "asc" },
  priceDesc: { field: "price", direction: "desc" },
  newest: { field: "updatedTime", direction: "desc" }
};

const sortOptionId = (sort: SourceCandidateSort): SortOptionId =>
  (Object.keys(SORT_OPTIONS) as SortOptionId[]).find(
    (id) =>
      SORT_OPTIONS[id].field === sort.field && SORT_OPTIONS[id].direction === sort.direction
  ) ?? "sameness";

type ListingSourceSearchDialogProps = {
  open: boolean;
  onClose: () => void;
  target: SourceCandidatesTarget;
  /** Links as the FORM holds them — the row's linked state, not the server's snapshot. */
  linkedIds: readonly string[];
  busyId: string | null;
  disabled?: boolean;
  onLink: (candidate: REST_schema.ListingSourceCandidate) => void;
  onUnlink: (sourceListingId: string) => void;
  /** Detach the ad from the listing that holds it, then attach it here. */
  onMoveLink: (candidate: REST_schema.ListingSourceCandidate, fromListingId: string) => void;
};

export default function ListingSourceSearchDialog(
  props: ListingSourceSearchDialogProps
): JSX.Element {
  const { open, onClose } = props;
  const { t: translate } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: { xs: "100%", sm: "86vh" }, maxHeight: "100%" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6" sx={{ minWidth: 0 }} noWrap>
            {translate("listingsPage.sourcePicker.dialogTitle")}
          </Typography>
          <IconButton size="small" onClick={onClose} edge="end">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      {/* Body mounts only while open — that is what makes every opening re-seed from the
          listing instead of resuming the previous session's narrowing. */}
      {open && <SearchBody {...props} />}
    </Dialog>
  );
}

function SearchBody({
  target,
  linkedIds,
  busyId,
  disabled,
  onLink,
  onUnlink,
  onMoveLink
}: ListingSourceSearchDialogProps): JSX.Element {
  const { t: translate } = useTranslation();
  const search = useSourceCandidatesSearch(target);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [showContradicted, setShowContradicted] = useState(false);

  const linked = useMemo(() => new Set(linkedIds), [linkedIds]);
  const holdingLabels = useHoldingListingLabels(search.candidates, target.listingId);

  /**
   * The accumulated list, re-ranked.
   *
   * ⚠ THE SAMENESS SCORE ORDERS A PAGE, NOT THE CORPUS — the endpoint says so, and
   * `loadMore` appends the next page verbatim. Under the default "best match" sort that
   * makes the list non-monotonic the moment she pages: page 2's 91% candidate lands
   * BELOW page 1's 12% one, and a realtor reading top-down stops before the better
   * match. Re-sorting what is on screen is honest rather than a papering-over — every
   * row carries its own score, so this reorders rows by a value they already state, and
   * it costs one comparison per row.
   *
   * Deliberately NOT applied to the other sorts: those are SQL orderings over the whole
   * match set, so appended pages are already monotonic and re-sorting could only
   * introduce a disagreement with what the server said.
   *
   * The comparator mirrors the server's exactly (confident, then score, then id) so a
   * single page renders in the order it arrived in.
   */
  const ordered = useMemo(() => {
    if (search.sort.field !== "sameness") return search.candidates;
    return [...search.candidates].sort(
      (a, b) =>
        Number(b.confident) - Number(a.confident) ||
        b.score - a.score ||
        a.id.localeCompare(b.id)
    );
  }, [search.candidates, search.sort.field]);

  /**
   * The split is done HERE, not by the server, and deliberately so: the toggle is then
   * instant and costs no request, the counts are exact for what is on screen, and the
   * server keeps one honest rule — it never silently drops a row.
   */
  const { matching, contradicting } = useMemo(() => {
    const matchingRows: REST_schema.ListingSourceCandidate[] = [];
    const contradictingRows: REST_schema.ListingSourceCandidate[] = [];
    ordered.forEach((candidate) => {
      (candidate.contradictions.length > 0 ? contradictingRows : matchingRows).push(candidate);
    });
    return { matching: matchingRows, contradicting: contradictingRows };
  }, [ordered]);

  const filtersEdited =
    Boolean(search.queryText.trim()) ||
    search.sort.field !== DEFAULT_CANDIDATE_SORT.field ||
    Boolean(
      search.filters && search.seedFilters && !areSearchFiltersEqual(search.filters, search.seedFilters)
    );

  const seededNames = search.seededChips.map((chip) =>
    translate(`listingsPage.sourcePicker.seededChip.${chip}`, chip)
  );
  const seedSummary = buildSeedSummary(search.seed);
  const showInitialSkeletons = !search.loadedOnce && !search.error;
  const showFirstError = search.error && !search.loadedOnce;

  const renderRow = (candidate: REST_schema.ListingSourceCandidate): JSX.Element => (
    <SourceCandidateRow
      key={candidate.id}
      candidate={candidate}
      seed={search.seed}
      // The FORM's links win over the server snapshot: a link made a second ago is
      // already in `value`, while the server's claim map is cached for 30s.
      linkedHere={linked.has(candidate.id)}
      holdingListingLabel={
        candidate.linkedTo ? holdingLabels[candidate.linkedTo.listingId] ?? null : null
      }
      busy={busyId === candidate.id}
      disabled={Boolean(disabled)}
      onLink={onLink}
      onUnlink={(row) => onUnlink(row.id)}
      onMoveLink={onMoveLink}
    />
  );

  return (
    <>
      {/* Controls sit OUTSIDE DialogContent so they stay put while the list scrolls. */}
      <Box sx={{ px: 3, pb: 1.5 }}>
        {seedSummary && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
            {translate("listingsPage.sourcePicker.dialogSubtitle", { summary: seedSummary })}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
          <TextField
            size="small"
            value={search.queryText}
            onChange={(event) => search.setQueryText(event.target.value)}
            placeholder={translate("listingsPage.sourcePicker.searchPlaceholder")}
            sx={{ minWidth: 240, flex: "1 1 260px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment:
                search.loading && search.loadedOnce ? <CircularProgress size={14} /> : undefined
            }}
          />
          <TextField
            select
            size="small"
            value={sortOptionId(search.sort)}
            onChange={(event) => search.setSort(SORT_OPTIONS[event.target.value as SortOptionId])}
            label={translate("listingsPage.sourcePicker.sortLabel")}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="sameness">{translate("listingsPage.sourcePicker.sort.sameness")}</MenuItem>
            <MenuItem value="priceAsc">{translate("listingsPage.sourcePicker.sort.priceAsc")}</MenuItem>
            <MenuItem value="priceDesc">{translate("listingsPage.sourcePicker.sort.priceDesc")}</MenuItem>
            <MenuItem value="newest">{translate("listingsPage.sourcePicker.sort.newest")}</MenuItem>
          </TextField>
        </Stack>

        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, mt: 1.5 }}>
          {/* Names what the server seeded, rather than leaving her to infer it from the
              chips' values. A seeded filter she does not know is there is an invisible
              narrowing — the exact failure that made composite keys unusable. */}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {filtersEdited || seededNames.length === 0
              ? translate("listingsPage.sourcePicker.filtersLabel")
              : translate("listingsPage.sourcePicker.filtersSeededLabel", {
                  fields: seededNames.join(", ")
                })}
          </Typography>
          {search.filters ? (
            /* The seeded values are ORDINARY, editable chips — a seeded filter that is
               not visible and not clearable is an invisible narrowing, which is exactly
               how the composite-key design silently excluded the true match. */
            /* `canResetToContact` is pinned FALSE and the reset is rendered here
               instead. MatchFilterChips hardcodes its reset as "איפוס לסינון הלקוח"
               with a tooltip about the customer's saved search profile — and there is
               no contact anywhere in this modal: the reset restores what was seeded
               from THE LISTING. The component is shared with the CRM matches tab,
               where that copy is correct, so it is suppressed from this side rather
               than changed. */
            <MatchFilterChips
              filters={search.filters}
              onChange={search.setFilters}
              onOpenAdvanced={() => setFiltersDrawerOpen(true)}
              canResetToContact={false}
              onResetToContact={search.resetToSeed}
            />
          ) : (
            <Stack direction="row" sx={{ gap: 1 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rectangular" width={78} height={30} sx={{ borderRadius: 999 }} />
              ))}
            </Stack>
          )}

          {/* The reset MatchFilterChips would have drawn, in this screen's own words.
              Shown only once she has actually narrowed, for the same reason the shared
              one is: on a freshly-seeded search it resets to the state already on
              screen. It also clears the free text and the sort, which is what
              `resetToSeed` does — hence "search", not "filters". */}
          {filtersEdited && search.filters && (
            <Tooltip title={translate("listingsPage.sourcePicker.resetToSeedHint") as string}>
              <Button
                size="small"
                onClick={search.resetToSeed}
                startIcon={<RestartAlt sx={{ fontSize: "16px !important" }} />}
                sx={{
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.25,
                  minHeight: 30,
                  typography: "caption",
                  fontWeight: 500,
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                {translate("listingsPage.sourcePicker.resetToSeed")}
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {search.filters && (
        <SourceCandidatesFiltersDrawer
          open={filtersDrawerOpen}
          filters={search.filters}
          onClose={() => setFiltersDrawerOpen(false)}
          onApply={search.setFilters}
        />
      )}

      <DialogContent dividers sx={{ pt: 2 }}>
        {showInitialSkeletons && (
          <Stack spacing={1}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={82} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        )}

        {showFirstError && (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              {translate("listingsPage.sourcePicker.searchFailed")}
            </Typography>
            <Button size="small" startIcon={<Replay />} onClick={search.retry} sx={{ mt: 1 }}>
              {translate("listingsPage.sourcePicker.retry")}
            </Button>
          </Box>
        )}

        {/* Nothing to narrow on. Deliberately NOT the same message as "no results": an
            unnarrowed scan would rank arbitrary rows from across the country, and calling
            that "no matches" is a claim she cannot act on. */}
        {!showInitialSkeletons && !showFirstError && search.needsNarrowing && (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              {translate("listingsPage.sourcePicker.needsNarrowing")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {translate("listingsPage.sourcePicker.needsNarrowingHint")}
            </Typography>
          </Box>
        )}

        {!showInitialSkeletons && !showFirstError && !search.needsNarrowing && (
          <Stack spacing={1}>
            {matching.map(renderRow)}

            {matching.length === 0 && contradicting.length === 0 && !search.loading && (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                  {translate("listingsPage.sourcePicker.noCandidates")}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {translate("listingsPage.sourcePicker.noCandidatesHint")}
                </Typography>
              </Box>
            )}

            {contradicting.length > 0 && (
              <Box sx={{ pt: matching.length > 0 ? 1 : 0 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setShowContradicted((shown) => !shown)}
                  sx={{ typography: "caption", color: "text.secondary" }}
                >
                  {showContradicted
                    ? translate("listingsPage.sourcePicker.hideContradicted")
                    : translate("listingsPage.sourcePicker.showContradicted", {
                        // `n`, not `count`: i18next treats `count` as the plural
                        // selector, and these strings have no plural forms.
                        n: contradicting.length
                      })}
                </Button>
                {showContradicted && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {translate("listingsPage.sourcePicker.contradictedExplainer")}
                    </Typography>
                    {contradicting.map(renderRow)}
                  </Stack>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {search.needsNarrowing || !search.loadedOnce
            ? ""
            : translate("listingsPage.sourcePicker.resultsCount", {
                shown: search.candidates.length,
                total: `${search.totalHits}${search.totalHitsCapped ? "+" : ""}`
              })}
        </Typography>
        <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<Tune sx={{ fontSize: "16px !important" }} />}
            disabled={!search.filters}
            onClick={() => setFiltersDrawerOpen(true)}
          >
            {translate("listingsPage.sourcePicker.allFilters")}
          </Button>
          {search.hasMore && (
            <Button
              size="small"
              variant="outlined"
              disabled={search.loadingMore}
              startIcon={search.loadingMore ? <CircularProgress size={13} /> : undefined}
              onClick={search.loadMore}
            >
              {translate("listingsPage.sourcePicker.loadMore")}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </>
  );
}

/**
 * "Seeded from: 4 rooms · 92 m² · הרצל 12 · חיפה · ₪2,300,000".
 *
 * The values the sameness rank is measured against, printed rather than implied. It is
 * what makes a low score legible: a listing that states only a city and a price cannot
 * produce a confident match, and saying so beats letting her wonder why every row is 8%.
 */
/**
 * "Which of my listings is holding this ad?" — as an address she recognises.
 *
 * The wire carries only the holding listing's ID, which names nothing, so the row could
 * previously say no more than "already linked to another of your listings". Resolving it
 * here rather than on the server is what keeps this a pure client change: the shared
 * candidate wire type is not ours to extend.
 *
 * `fetchListingById` (the signatures fetcher) is reused deliberately — it reads the
 * mirror and FALLS BACK to `real-estate-listings`, which is the case that matters: the
 * mirror row exists only while a listing is `onSale`, and a `sold` copy holding the ad
 * is precisely the one she is hunting for. It also dedupes per id across the page, so
 * re-running this on every appended page costs nothing.
 *
 * A listing whose read fails, or that has no address at all, simply produces no entry —
 * the row falls back to the unnamed wording rather than rendering an empty link.
 */
function useHoldingListingLabels(
  candidates: readonly REST_schema.ListingSourceCandidate[],
  thisListingId: string | undefined
): Record<string, string> {
  const [labels, setLabels] = useState<Record<string, string>>({});

  // A stable string key, so appending a page that introduces no NEW holder does not
  // re-run the effect.
  const idsKey = useMemo(() => {
    const ids = new Set<string>();
    candidates.forEach((candidate) => {
      const link = candidate.linkedTo;
      if (!link || link.isThisListing) return;
      if (!link.listingId || link.listingId === thisListingId) return;
      ids.add(link.listingId);
    });
    return [...ids].sort().join("|");
  }, [candidates, thisListingId]);

  useEffect(() => {
    const wanted = idsKey ? idsKey.split("|") : [];
    if (wanted.length === 0) return undefined;
    let cancelled = false;

    void Promise.all(
      wanted.map(async (listingId) => {
        const listing = await fetchListingById(listingId);
        const address = listing?.address;
        const street = [address?.street, address?.houseNumber].filter(Boolean).join(" ");
        return [listingId, [street, address?.city].filter(Boolean).join(", ")] as const;
      })
    )
      .then((entries) => {
        if (cancelled) return;
        setLabels((prev) => {
          const next = { ...prev };
          entries.forEach(([listingId, label]) => {
            if (label) next[listingId] = label;
          });
          return next;
        });
      })
      .catch((error) => {
        console.error("ListingSourcePicker: could not name the holding listings", error);
      });

    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return labels;
}

function buildSeedSummary(seed: REST_schema.ListingSourceCandidateSeed | null): string {
  if (!seed) return "";
  const street = [seed.street, seed.streetNumber].filter((part) => part != null && part !== "").join(" ");
  return [
    street || null,
    seed.city,
    seed.rooms != null ? `${seed.rooms} חד׳` : null,
    seed.sizeOfPropertyM2 != null ? `${seed.sizeOfPropertyM2} מ״ר` : null,
    seed.price ? numberToCurrency(seed.price) : null
  ]
    .filter(Boolean)
    .join(" · ");
}
